import { Box, Typography, Button } from "@mui/material";
import { Add } from "@mui/icons-material";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import React, { useEffect, useState, useRef } from "react";
import { defaultParams } from "../../App";
import AcrInputs from "./AcrInputs";
import FormWrapper from "./FormWrapper";
import MortgageTypeInput from "./MortgageTypeInput";
import RepaymentInputs from "./RepaymentInputs";
import SavingsInputs from "./SavingsInputs";
import LoanAmountEntry from "./LoanAmountEntry";
import {
  SimulationFormProps,
  SimulationFormInputs,
  SimulationFormParams,
} from "../../types/forms";
import SubmitButton from "./SubmitButton";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

const demoParams: SimulationFormInputs = {
  principal: 0,
  interestOnly: false,
  termYears: 25,
  interestRate: 4.5,
  desiredMonthlyPayment: undefined,
  additionalCapitalRepayments: false,
  dealLength: 24,
  maxAffordable: 2000.0,
  savingsInterestRate: 3.5,
  saveAtStart: false,
  additionalCapitalRepaymentPercentageLimit: 10,
  reduceTermOnAdditionalCapitalRepayment: false,
  savingsAccounts: [],
};

export default function LoanAmountSimulationForm({
  model,
  onSubmit,
  onCancel,
  isEditing,
  editingIndex,
}: SimulationFormProps) {
  const [localIndices, setLocalIndices] = useState<number[]>([]);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const hasInitialized = useRef<boolean>(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = localIndices.findIndex(
        (idx) => `loan-${idx}` === active.id,
      );
      const newIndex = localIndices.findIndex(
        (idx) => `loan-${idx}` === over.id,
      );

      if (oldIndex !== -1 && newIndex !== -1) {
        // Move the simulation in the model - this persists the order
        // The SortableContext handles the visual reordering, so we don't
        // need to update localIndices since they stay as [0, 1, 2, ...]
        model.moveSimulation(oldIndex, newIndex);
      }
    }
  };

  useEffect(() => {
    if (!isEditing && !hasInitialized.current) {
      hasInitialized.current = true;
      const newSim1: SimulationFormParams = {
        inputs: { ...defaultParams, principal: 0 },
        updated: true,
      };
      const newSim2: SimulationFormParams = {
        inputs: { ...defaultParams, principal: 0 },
        updated: true,
      };
      model.addSimulation(newSim1);
      model.addSimulation(newSim2);
      setLocalIndices([0, 1]);
      setIsInitialized(true);
    } else if (isEditing && !hasInitialized.current) {
      const existingIndices = model.simulations.map((_, i) => i);
      setLocalIndices(existingIndices);
      setIsInitialized(true);
      hasInitialized.current = true;
    }
    // This should only run once when the form is mounted for adding new simulations
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCancel = () => {
    if (!isEditing && localIndices.length > 0) {
      // Remove in reverse order to avoid index shifting issues
      [...localIndices].reverse().forEach((idx) => {
        model.removeSimulation(idx);
      });
    }
    onCancel();
  };

  const handleAddSimulation = () => {
    const params = model.simulations[localIndices[0]]?.inputs || defaultParams;
    const newSim: SimulationFormParams = {
      inputs: { ...params, principal: 0 },
      updated: true,
    };
    model.addSimulation(newSim);
    setLocalIndices([...localIndices, model.simulations.length]);
  };

  const handleRemoveSimulation = (indexInLocalArray: number) => {
    if (localIndices.length <= 2) return; // Keep at least 2 simulations

    const actualIndex = localIndices[indexInLocalArray];
    model.removeSimulation(actualIndex);

    // Update local indices - remove the deleted one and adjust others
    const newLocalIndices = localIndices
      .filter((_, i) => i !== indexInLocalArray)
      .map((idx) => (idx > actualIndex ? idx - 1 : idx));

    setLocalIndices(newLocalIndices);
  };

  const handleDemo = () => {
    // Clear all existing simulations
    if (localIndices.length > 0) {
      [...localIndices].reverse().forEach((idx) => {
        model.removeSimulation(idx);
      });
    }

    // Add two new simulations with demo parameters
    const newSim1: SimulationFormParams = {
      inputs: { ...demoParams, principal: 200000 },
      updated: true,
    };
    const newSim2: SimulationFormParams = {
      inputs: { ...demoParams, principal: 300000 },
      updated: true,
    };
    model.addSimulation(newSim1);
    model.addSimulation(newSim2);
    setLocalIndices([
      model.simulations.length - 2,
      model.simulations.length - 1,
    ]);
  };

  // Wait for initialization before rendering form
  if (!isInitialized) {
    return null;
  }

  if (localIndices == null) {
    return null;
  }

  return (
    <FormWrapper onSubmit={onSubmit}>
      {(error) => (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6">
              {isEditing ? "Update Simulations" : "Create Simulations"}
            </Typography>
            <Button
              startIcon={<AutoFixHighIcon />}
              onClick={handleDemo}
              variant="outlined"
              size="small"
            >
              Demo
            </Button>
          </Box>

          <Box sx={{ mt: 2, mb: 2 }}>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={localIndices.map((idx) => `loan-${idx}`)}
                strategy={verticalListSortingStrategy}
              >
                {localIndices.map((actualIndex, i) => {
                  const formInputs = model.simulations[actualIndex]?.inputs || {
                    ...defaultParams,
                    principal: 0,
                  };
                  return (
                    <LoanAmountEntry
                      key={actualIndex}
                      id={`loan-${actualIndex}`}
                      index={i}
                      value={formInputs.principal}
                      onChange={(val) =>
                        model.updateSimulation(
                          actualIndex,
                          {},
                          { principal: val },
                        )
                      }
                      onRemove={() => handleRemoveSimulation(i)}
                      canRemove={localIndices.length > 2}
                      autoFocus={editingIndex === actualIndex}
                    />
                  );
                })}
              </SortableContext>
            </DndContext>
            <Button
              startIcon={<Add />}
              onClick={handleAddSimulation}
              variant="outlined"
              fullWidth
              sx={{ mt: 1 }}
            >
              Add Simulation
            </Button>
          </Box>

          <MortgageTypeInput
            interestOnly={
              model.simulations[localIndices[0]]?.inputs.interestOnly
            }
            interestRate={
              model.simulations[localIndices[0]]?.inputs.interestRate
            }
            desiredMonthlyPayment={
              model.simulations[localIndices[0]]?.inputs.desiredMonthlyPayment
            }
            onChange={(updates: Partial<SimulationFormInputs>) => {
              localIndices.forEach((idx) => {
                model.updateSimulation(idx, {}, updates);
              });
            }}
          />

          <RepaymentInputs
            interestOnly={
              model.simulations[localIndices[0]]?.inputs.interestOnly
            }
            termYears={model.simulations[localIndices[0]]?.inputs.termYears}
            interestRate={
              model.simulations[localIndices[0]]?.inputs.interestRate
            }
            desiredMonthlyPayment={
              model.simulations[localIndices[0]]?.inputs.desiredMonthlyPayment
            }
            onChange={(updates: Partial<SimulationFormInputs>) => {
              localIndices.forEach((idx) => {
                model.updateSimulation(idx, {}, updates);
              });
            }}
          />

          <AcrInputs
            additionalCapitalRepayments={
              model.simulations[localIndices[0]]?.inputs
                .additionalCapitalRepayments
            }
            dealLength={model.simulations[localIndices[0]]?.inputs.dealLength}
            maxAffordable={
              model.simulations[localIndices[0]]?.inputs.maxAffordable
            }
            savingsInterestRate={
              model.simulations[localIndices[0]]?.inputs.savingsInterestRate
            }
            saveAtStart={model.simulations[localIndices[0]]?.inputs.saveAtStart}
            additionalCapitalRepaymentPercentageLimit={
              model.simulations[localIndices[0]]?.inputs
                .additionalCapitalRepaymentPercentageLimit
            }
            reduceTermOnAdditionalCapitalRepayment={
              model.simulations[localIndices[0]]?.inputs
                .reduceTermOnAdditionalCapitalRepayment
            }
            skipAdditionalCapitalRepayments={
              model.simulations[localIndices[0]]?.inputs
                .skipAdditionalCapitalRepayments
            }
            interestOnly={
              model.simulations[localIndices[0]]?.inputs.interestOnly
            }
            onChange={(updates: Partial<SimulationFormInputs>) => {
              localIndices.forEach((idx) => {
                model.updateSimulation(idx, {}, updates);
              });
            }}
          />

          <SavingsInputs
            accounts={
              model.simulations[localIndices[0]]?.inputs.savingsAccounts
            }
            onChange={(updates: Partial<SimulationFormInputs>) => {
              localIndices.forEach((idx) => {
                model.updateSimulation(idx, {}, updates);
              });
            }}
          />

          <SubmitButton
            isEditing={isEditing}
            multipleSimulations={true}
            error={error}
            onCancel={handleCancel}
          />
        </>
      )}
    </FormWrapper>
  );
}
